import { displayPlaceName, ROUTE_LANDING_BY_SLUG, toRoutePath } from './routeLanding';
import { FEATURED_ROUTE_LANDING_PAGES } from './featuredRoutes';
import { LOCAL_CORRIDOR_PAGES } from './localCorridorPages';

const DEDICATED_ROUTE_PATHS = new Set([
  '/chennai-to-madurai-taxi',
  '/madurai-to-chennai-taxi',
]);

export const resolveExistingRoutePath = (origin, destination) => {
  const slug = toRoutePath(origin, destination);
  if (DEDICATED_ROUTE_PATHS.has(slug)) return slug;
  if (ROUTE_LANDING_BY_SLUG[slug]) return slug;

  const featured = FEATURED_ROUTE_LANDING_PAGES.find(
    (page) => page.origin === origin && page.destination === destination,
  );
  if (featured?.slug) return featured.slug;

  const corridor = LOCAL_CORRIDOR_PAGES.find(
    (page) => page.origin === origin && page.destination === destination,
  );
  if (corridor?.slug) return corridor.slug;

  return null;
};

const attachRoutePaths = (route) => ({
  origin: route.origin,
  destination: route.destination,
  note: route.note,
  label: `${displayPlaceName(route.origin)} to ${displayPlaceName(route.destination)} Taxi`,
  path: resolveExistingRoutePath(route.origin, route.destination),
  reversePath: resolveExistingRoutePath(route.destination, route.origin),
  plannedPath: toRoutePath(route.origin, route.destination),
});

const HUB_SOURCE = [
  {
    name: 'Coimbatore',
    slug: '/coimbatore-taxi',
    pickupName: 'Coimbatore',
    h1: 'Coimbatore Taxi',
    seoTitle: 'Coimbatore Taxi | One Way Cab | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Coimbatore with Easy Drop Taxi. Pickup starts in Coimbatore, you choose the drop, and the fare uses the same live estimate as the homepage booking form.',
    heroLead:
      'Coimbatore is Easy Drop Taxi’s home city. Use this hub when you need a one-way cab out of Coimbatore rather than browsing the full homepage.',
    intro: [
      'Easy Drop Taxi is based in Coimbatore. This city hub is for outbound one-way bookings that start here: you keep or edit Coimbatore as pickup, then name the drop yourself.',
      'Dedicated Coimbatore route pages already cover Chennai, Bangalore, Madurai, Trichy, Ooty, and local corridors such as Karur, Pollachi and Tiruppur. Open those pair pages when the drop is already chosen; use this form if you still need to pick a destination.',
      'One Way is the default so you are not holding a return vehicle in Coimbatore. Switch to Round Trip in the booking card only if you need both legs.',
    ],
    serviceNote:
      'Coimbatore and Coimbatore Airport already appear in Easy Drop Taxi’s city list. Type a more specific pickup if you are leaving from home, a hotel, Singanallur, or the airport. This page does not invent a neighbourhood coverage catalogue.',
    benefits: [
      {
        title: 'Home-city outbound booking',
        text: 'Start in Coimbatore when you need an outstation or corridor drop instead of a local hire that stays in the city.',
      },
      {
        title: 'Existing route pages',
        text: 'Priority links below open live Coimbatore pair pages already on the site, including Chennai, Bangalore, Madurai, Trichy and Ooty.',
      },
      {
        title: 'Airport or locality pickup',
        text: 'Change pickup to Coimbatore Airport or another mapped Coimbatore point before you estimate. Singanallur also has its own landing page.',
      },
      {
        title: 'Same booking form',
        text: 'Vehicle choice, date, time and confirmation use the homepage Easy Drop Taxi flow. Nothing on this page is a parallel booking system.',
      },
    ],
    howTo: [
      'Keep or edit Coimbatore as pickup, enter the drop city or address, add your 10-digit mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta, then add the date and time you want to leave Coimbatore and your name.',
      'Review the trip and fare, then confirm. The request uses the existing Easy Drop Taxi booking system.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Coimbatore?',
        answer:
          'On this Coimbatore hub, pickup starts as Coimbatore. Enter any drop — including Chennai, Bangalore, Madurai, Trichy, Ooty or a local corridor — add your 10-digit mobile number, then tap Get Fare Estimate. Choose Sedan, SUV or Crysta with your leave date and time, and confirm the review.',
      },
      {
        question: 'Is this the same as the Easy Drop Taxi homepage?',
        answer:
          'The homepage remains the main Coimbatore marketing and booking entry. This /coimbatore-taxi hub is the city landing for travellers searching specifically for Coimbatore taxi, with the same form and published tariff.',
      },
      {
        question: 'Which Coimbatore routes already have their own pages?',
        answer:
          'Coimbatore to Chennai, Bangalore, Madurai, Trichy and Ooty are linked below when those pair pages exist. Local corridors such as Karur, Pollachi and Tiruppur also have their own URLs. Other drops can still be booked from this hub.',
      },
      {
        question: 'How is the Coimbatore taxi fare calculated?',
        answer:
          'The form uses billed distance × vehicle rate + driver bata, with the same one-way minimum used elsewhere on the site. Distance comes from the live mapped drive for the pickup and drop you enter, not from inventing a package fare on this page.',
      },
      {
        question: 'Can I start from Coimbatore Airport or Singanallur?',
        answer:
          'Yes. Change pickup to Coimbatore Airport or another mapped address. For Singanallur-focused copy, open the Singanallur Taxi page, or type Singanallur in pickup here.',
      },
    ],
    ctaTitle: 'Ready to book a taxi from Coimbatore?',
    ctaBody: 'Enter your drop in the booking card and get a live fare estimate with EasyDropTaxi.',
    priorityRoutes: [
      { origin: 'Coimbatore', destination: 'Chennai', note: 'Long eastbound outstation run with its own landing page.' },
      { origin: 'Coimbatore', destination: 'Bangalore', note: 'Interstate drop with an existing Coimbatore catalogue pair.' },
      { origin: 'Coimbatore', destination: 'Madurai', note: 'Southbound temple-city drop with a live pair URL.' },
      { origin: 'Coimbatore', destination: 'Trichy', note: 'Central Tamil Nadu drop with a live pair URL.' },
      { origin: 'Coimbatore', destination: 'Ooty', note: 'Hill-station featured route with its own Coimbatore–Ooty page.' },
    ],
  },
  {
    name: 'Chennai',
    slug: '/chennai-taxi',
    pickupName: 'Chennai',
    h1: 'Chennai Taxi',
    seoTitle: 'Chennai Taxi | One Way Cab | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Chennai with Easy Drop Taxi. Start in Chennai, add your drop, and estimate the fare in the same online booking form used across the site.',
    heroLead:
      'Leave Chennai in a one-way cab when you need an outstation drop rather than a local hire that stays in the city.',
    intro: [
      'Chennai is where many Easy Drop Taxi trips begin when travellers need to leave the capital for another Tamil Nadu city. This page is for that outbound booking: pickup starts in Chennai, and you name the drop yourself.',
      'The dedicated Chennai to Madurai and Chennai to Coimbatore landing pages already exist, along with Chennai to Tirunelveli, Kanyakumari and Rameshwaram. Open those pair pages when that is your drop; use this form if you still need to choose a destination.',
      'One Way is the default so you are not holding a return vehicle in Chennai. Switch to Round Trip in the booking card only if you need both legs.',
    ],
    serviceNote:
      'Chennai and Chennai Airport already appear in Easy Drop Taxi’s city list. Type a more specific pickup if you are leaving from home, a hotel or the airport. Neighbourhoods are not listed here as a separate coverage catalogue.',
    benefits: [
      {
        title: 'Airport or city pickup',
        text: 'Start as Chennai or change the field to Chennai Airport if that is where you will wait. Both names already exist in the service city list.',
      },
      {
        title: 'Long southbound trips',
        text: 'Chennai is a common origin for Madurai, Coimbatore and further south. The fare still follows the published per-kilometre tariff, not a Chennai package.',
      },
      {
        title: 'One-way out of the capital',
        text: 'Book only the outbound leg when you will not need the same cab back into Chennai the same day.',
      },
      {
        title: 'Same booking form',
        text: 'Vehicle choice, date, time and confirmation use the homepage Easy Drop Taxi flow. Nothing on this page is a parallel booking system.',
      },
    ],
    howTo: [
      'Keep or edit Chennai as pickup, enter the drop city or address, add your 10-digit mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta, then add the date and time you want to leave Chennai and your name.',
      'Review the trip and fare, then confirm. The request uses the existing Easy Drop Taxi booking system.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Chennai?',
        answer:
          'This Chennai hub pre-fills pickup in the capital. Set a Tamil Nadu drop such as Madurai, Coimbatore, Tirunelveli, Kanyakumari or Rameshwaram — or another mapped address — add your mobile number, then Get Fare Estimate. Finish with vehicle, leave date and time before you confirm.',
      },
      {
        question: 'Can I book a one-way taxi from Chennai Airport?',
        answer:
          'Yes. Change pickup to Chennai Airport or another airport address, keep your drop, and continue with the same estimate and confirmation steps.',
      },
      {
        question: 'Which Chennai routes already have their own pages?',
        answer:
          'Chennai to Madurai, Coimbatore, Tirunelveli, Kanyakumari and Rameshwaram each have their own landing pages. Open those for route-specific copy. Other Chennai drops can still be booked from this hub.',
      },
      {
        question: 'How is the Chennai taxi fare calculated?',
        answer:
          'The form uses billed distance × vehicle rate + driver bata, with a 130 km one-way minimum. Distance comes from the live mapped drive for the pickup and drop you enter, not from a catalogue kilometre on this page.',
      },
      {
        question: 'Can I edit the Chennai pickup later?',
        answer:
          'Yes. Pickup is only a starting value. You can type a neighbourhood, hotel or mapped point before you search.',
      },
    ],
    ctaTitle: 'Ready to book a taxi from Chennai?',
    ctaBody: 'Enter your drop in the booking card and get a live fare estimate with EasyDropTaxi.',
    priorityRoutes: [
      { origin: 'Chennai', destination: 'Madurai', note: 'Temple-city drop with its own landing page.' },
      { origin: 'Chennai', destination: 'Coimbatore', note: 'Westbound outstation run toward Easy Drop Taxi’s home city.' },
      { origin: 'Chennai', destination: 'Tirunelveli', note: 'South Tamil Nadu drop with its own landing page.' },
      { origin: 'Chennai', destination: 'Kanyakumari', note: 'Coastal end-of-state drop with a dedicated pair URL.' },
      { origin: 'Chennai', destination: 'Rameshwaram', note: 'Island-side drop from Chennai, separate from the Coimbatore pair.' },
    ],
  },
  {
    name: 'Madurai',
    slug: '/madurai-taxi',
    pickupName: 'Madurai',
    h1: 'Madurai Taxi',
    seoTitle: 'Madurai Taxi | Outstation Cab | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Madurai with Easy Drop Taxi. Pickup starts in Madurai, you choose the drop, and the fare uses the existing live estimate in the online form.',
    heroLead:
      'Use Madurai as the starting city when you need a one-way cab out, including the existing Madurai to Chennai, Coimbatore, Bangalore, Vellore and Tirupati route pages.',
    intro: [
      'Madurai sits in the south of the state, so many bookings from here are north or west toward Chennai, Coimbatore or Karnataka. This hub is for leaving Madurai rather than arriving into it.',
      'Madurai to Chennai already has its own dedicated page. Madurai to Coimbatore, Bangalore, Vellore and Tirupati now have pair URLs as well. Open those when that is your drop; use this form if you still need to choose a destination.',
      'Drop stays empty until you type it, so a local Madurai drop and a long outstation drop use the same form.',
    ],
    serviceNote:
      'Madurai is in Easy Drop Taxi’s existing city list. You can still replace the default with a hotel, the airport or another mapped point. This page does not invent a neighbourhood coverage list.',
    benefits: [
      {
        title: 'Temple-city outbound',
        text: 'Book when your stay in Madurai is finished and you need a cab that continues to another city instead of a local round.',
      },
      {
        title: 'Existing Chennai reverse',
        text: 'The Madurai to Chennai landing page is already live if that is the drop. Coimbatore, Bangalore, Vellore and Tirupati each have their own Madurai-origin pair pages too.',
      },
      {
        title: 'Editable Madurai pickup',
        text: 'The city name is only the default. Change it to a more specific Madurai address before you estimate.',
      },
      {
        title: 'Published tariff only',
        text: 'Sedan, SUV and Crysta use the same per-kilometre rates and driver bata as the rest of Easy Drop Taxi. There is no Madurai package fare.',
      },
    ],
    howTo: [
      'Keep Madurai as pickup or edit it, type the drop city, add your mobile number, then request a fare estimate.',
      'Pick a vehicle and the time you want to leave Madurai, then add your name.',
      'Confirm the review so the request goes through the same booking process as the homepage.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Madurai?',
        answer:
          'Use the booking card on this page. Pickup is pre-filled as Madurai. Enter drop and mobile number, tap Get Fare Estimate, then choose vehicle, date and time before you confirm.',
      },
      {
        question: 'Is there a Madurai to Chennai taxi page?',
        answer:
          'Yes. Open Madurai to Chennai Taxi for that specific pair. This hub is for other Madurai pickups and for travellers who have not chosen a drop yet.',
      },
      {
        question: 'Can I book Madurai to Coimbatore from here?',
        answer:
          'You can still set drop to Coimbatore in this form. There is also a dedicated Madurai to Coimbatore Taxi page if that is already your pair. Do not use a locked Coimbatore-origin catalogue page as a substitute when you are leaving Madurai.',
      },
      {
        question: 'Does this page show a fixed Madurai fare?',
        answer:
          'No. Distance and total come from the pickup and drop you enter, using the live mapping already used on other Easy Drop Taxi bookings.',
      },
      {
        question: 'Can I start from Madurai Airport?',
        answer:
          'Yes, if that is your actual pickup. Replace Madurai in the pickup field with the airport name or address, then continue.',
      },
    ],
    ctaTitle: 'Ready to leave Madurai by taxi?',
    ctaBody: 'Add your drop in the booking card and confirm through EasyDropTaxi’s existing form.',
    priorityRoutes: [
      { origin: 'Madurai', destination: 'Chennai', note: 'Northbound capital run with a dedicated reverse of the Chennai page.' },
      { origin: 'Madurai', destination: 'Coimbatore', note: 'West toward Easy Drop Taxi’s home city, with its own pair URL.' },
      { origin: 'Madurai', destination: 'Bangalore', note: 'Interstate drop with a dedicated Madurai–Bangalore landing page.' },
      { origin: 'Madurai', destination: 'Vellore', note: 'Northern Tamil Nadu drop with its own pair URL.' },
      { origin: 'Madurai', destination: 'Tirupati', note: 'Andhra temple-town drop, separate from Coimbatore’s Tripati catalogue slug.' },
    ],
  },
  {
    name: 'Trichy',
    slug: '/trichy-taxi',
    pickupName: 'Trichy',
    h1: 'Trichy Taxi',
    seoTitle: 'Trichy Taxi | One Way Cab | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Trichy (Tiruchirappalli) with Easy Drop Taxi. Pickup starts in Trichy, you enter the drop, and the fare uses the existing online estimate.',
    heroLead:
      'Trichy is a central Tamil Nadu start point. Book a one-way cab when you are leaving the city rather than hiring for local errands.',
    intro: [
      'Tiruchirappalli sits on rail, road and air links, so travellers often need a cab that continues to Chennai, Bangalore, Coimbatore, Madurai or Kanyakumari after they finish in Trichy.',
      'Trichy to Chennai, Bangalore, Coimbatore, Madurai and Kanyakumari now have pair URLs. Open those when that is your drop; use this form if you still need to choose a destination.',
      'Pickup defaults to Trichy because that is the name used for this hub. You can type Tiruchirappalli or a more specific address if that matches how you want the field mapped.',
    ],
    serviceNote:
      'Type Trichy, Tiruchirappalli or a mapped Trichy address in pickup. This hub does not list unofficial neighbourhood coverage or claim a separate Trichy fleet.',
    benefits: [
      {
        title: 'Central Tamil Nadu origin',
        text: 'Trichy is a practical start for both coastal and western drops. The booking still uses one vehicle from your pickup to the drop you enter.',
      },
      {
        title: 'Airport-friendly field',
        text: 'If you are leaving from Trichy Airport, replace the default pickup with that airport name before you estimate.',
      },
      {
        title: 'Priority pair pages',
        text: 'The five Trichy-origin routes below each have a landing page. Open the pair URL when the drop is already chosen.',
      },
      {
        title: 'Same tariff as other cities',
        text: 'Rates stay ₹15 / ₹20 / ₹22 per km plus driver bata. Trichy does not get a different unpublished price list.',
      },
    ],
    howTo: [
      'Confirm Trichy (or a more specific Tiruchirappalli address) as pickup, enter drop and mobile number, then get the estimate.',
      'Select Sedan, SUV or Crysta and the time you want to leave Trichy.',
      'Review extras such as toll or permit on the confirmation screen, then send the request.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Trichy?',
        answer:
          'Use this page’s booking card. Pickup defaults to Trichy. Add drop and mobile number, estimate the fare, then choose vehicle, date and time.',
      },
      {
        question: 'Should I type Trichy or Tiruchirappalli?',
        answer:
          'Either can work in the location field. The hub uses Trichy as the default label. Change it if Google Places suggests a more precise point for your pickup.',
      },
      {
        question: 'Do the priority Trichy routes have their own pages?',
        answer:
          'Yes. Trichy to Chennai, Bangalore, Coimbatore, Madurai and Kanyakumari each have a landing page. This hub is for other Trichy pickups and for travellers who have not chosen a drop yet.',
      },
      {
        question: 'Can I book Trichy to Chennai as one way?',
        answer:
          'Yes. The dedicated Trichy to Chennai Taxi page is the best match for that eastbound pair. You can also set drop to Chennai here and keep One Way selected so the cab finishes in Chennai rather than returning to Trichy.',
      },
      {
        question: 'Is there a fixed Trichy taxi package?',
        answer:
          'No. The total follows the live mapped distance for your two points, the published vehicle rate and driver bata, plus extras shown at review.',
      },
    ],
    ctaTitle: 'Ready to book a taxi from Trichy?',
    ctaBody: 'Enter your drop from Trichy and confirm with EasyDropTaxi’s existing booking form.',
    priorityRoutes: [
      { origin: 'Trichy', destination: 'Chennai', note: 'East toward the capital, with its own pair URL.' },
      { origin: 'Trichy', destination: 'Bangalore', note: 'Interstate drop from central Tamil Nadu, with a dedicated landing page.' },
      { origin: 'Trichy', destination: 'Coimbatore', note: 'West toward Easy Drop Taxi’s home city, separate from the Coimbatore catalogue.' },
      { origin: 'Trichy', destination: 'Madurai', note: 'Southbound temple-city drop with its own pair URL.' },
      { origin: 'Trichy', destination: 'Kanyakumari', note: 'Long southbound coastal drop, separate from Coimbatore’s kaniyakumari slug.' },
    ],
  },
  {
    name: 'Bangalore',
    slug: '/bangalore-taxi',
    pickupName: 'Bangalore',
    h1: 'Bangalore Taxi',
    seoTitle: 'Bangalore Taxi | Cab to Tamil Nadu | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Bangalore with Easy Drop Taxi. Pickup starts in Bangalore, you set a Tamil Nadu drop, and the fare uses the existing live estimate.',
    heroLead:
      'Start in Bangalore when you need a one-way cab into Tamil Nadu, including the Bangalore to Coimbatore, Chennai, Madurai, Trichy and Salem route pages.',
    intro: [
      'Bangalore is the interstate origin on this site. Easy Drop Taxi is based in Tamil Nadu, so this hub is for leaving Karnataka toward Coimbatore, Chennai, Madurai, Trichy or Salem — not for a Bengaluru local hire.',
      'Bangalore to Coimbatore, Chennai, Madurai, Trichy and Salem now have pair URLs. Open those when that is your drop; use this form if you still need to choose a destination.',
      'Pickup can stay as Bangalore or change to Bangalore Airport, which already exists in the homepage city list.',
    ],
    serviceNote:
      'Bangalore and Bangalore Airport are in the existing city list. State permit and toll, where they apply on an interstate drive, remain extra as already noted on booking review.',
    benefits: [
      {
        title: 'Interstate one-way',
        text: 'Book when you will leave the cab in Tamil Nadu and do not need it held in Bangalore overnight.',
      },
      {
        title: 'Priority pair pages',
        text: 'The five Bangalore-origin routes below each have a landing page. Open the pair URL when the drop is already chosen.',
      },
      {
        title: 'Airport pickup supported as a field edit',
        text: 'Replace Bangalore with Bangalore Airport when you are waiting at the terminal. That name is already in the city list.',
      },
      {
        title: 'No Bangalore package rate',
        text: 'The estimate still uses billed kilometres, the published vehicle rate and driver bata. This page does not invent an interstate lump sum.',
      },
    ],
    howTo: [
      'Keep Bangalore or switch pickup to Bangalore Airport, enter the Tamil Nadu drop, add your mobile number, then estimate.',
      'Choose the vehicle and the time you want to leave Karnataka.',
      'Check permit and toll notes on the review screen before you confirm.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Bangalore?',
        answer:
          'Use the booking card on this page. Pickup defaults to Bangalore. Enter a drop in Tamil Nadu, add your mobile number, then Get Fare Estimate and complete vehicle, date and time.',
      },
      {
        question: 'Is Bangalore the same as Bengaluru on this site?',
        answer:
          'Yes. Bengaluru is the official city name. Easy Drop Taxi keeps Bangalore in URLs, city-list labels and booking fields so they match the existing live pages such as /bangalore-taxi. There is no separate Bengaluru taxi URL.',
      },
      {
        question: 'Do the priority Bangalore routes have their own pages?',
        answer:
          'Yes. Bangalore to Coimbatore, Chennai, Madurai, Trichy and Salem each have a landing page. This hub is for other Bangalore pickups and for travellers who have not chosen a drop yet.',
      },
      {
        question: 'Can I book Bangalore to Chennai as one way?',
        answer:
          'Yes. Use Bangalore to Chennai Taxi when that interstate pair is fixed. From this hub you can also set drop to Chennai and keep One Way selected so the cab ends in Chennai without a Karnataka return leg.',
      },
      {
        question: 'Are interstate permits included in the estimate?',
        answer:
          'The on-screen total follows distance, rate and driver bata. Toll, state permit, parking and waiting time are extra, matching the note already shown when you review a booking.',
      },
      {
        question: 'Can I start from Bangalore Airport?',
        answer:
          'Yes. Change pickup to Bangalore Airport or a terminal address, then estimate as usual.',
      },
    ],
    ctaTitle: 'Ready to book a taxi from Bangalore?',
    ctaBody: 'Set your Tamil Nadu drop and estimate with EasyDropTaxi’s existing booking form.',
    priorityRoutes: [
      { origin: 'Bangalore', destination: 'Coimbatore', note: 'Existing Coimbatore catalogue reverse for this interstate pair.' },
      { origin: 'Bangalore', destination: 'Chennai', note: 'East to the Tamil Nadu capital, with its own pair URL.' },
      { origin: 'Bangalore', destination: 'Madurai', note: 'Southbound interstate drop with a dedicated landing page.' },
      { origin: 'Bangalore', destination: 'Trichy', note: 'Central Tamil Nadu drop, with a dedicated landing page.' },
      { origin: 'Bangalore', destination: 'Salem', note: 'NH-side drop into Tamil Nadu, with its own pair URL.' },
    ],
  },
  {
    name: 'Salem',
    slug: '/salem-taxi',
    pickupName: 'Salem',
    h1: 'Salem Taxi',
    seoTitle: 'Salem Taxi | One Way Cab | Easy Drop Taxi',
    seoDescription:
      'Book a one-way taxi from Salem with Easy Drop Taxi. Pickup starts in Salem, you enter the drop, and the fare uses the same live estimate as other Easy Drop Taxi bookings.',
    heroLead:
      'Salem sits on the main north–south road through Tamil Nadu. Book a one-way cab when you are leaving the city for Chennai, Coimbatore, Bangalore, Madurai or Trichy.',
    intro: [
      'Salem is a highway city between Chennai, Coimbatore and Bangalore, so one-way bookings from here are often longer than a local drop. This page is the Salem origin hub, not a neighbourhood taxi stand list.',
      'Salem to Chennai, Coimbatore, Bangalore, Madurai and Trichy now have pair URLs. Open those when that is your drop; use this form if you still need to choose a destination.',
      'Pickup defaults to Salem. Change it if you are starting from a factory, hotel or another mapped point in or around the city.',
    ],
    serviceNote:
      'Type Salem or a more specific mapped address. Chinna Salem is a different place in the city list — do not use it unless that is actually your pickup.',
    benefits: [
      {
        title: 'Highway origin',
        text: 'Salem is a practical start toward Chennai, Coimbatore or Bangalore. You still travel in one booked vehicle to the drop you enter.',
      },
      {
        title: 'Priority pair pages',
        text: 'The five Salem-origin routes below each have a landing page. Open the pair URL when the drop is already chosen.',
      },
      {
        title: 'One-way by default',
        text: 'Useful when you will not need the same cab back into Salem the same day. Round Trip remains available in the form.',
      },
      {
        title: 'Existing tariff',
        text: 'Sedan, SUV and Crysta follow the published per-kilometre rates plus driver bata. Salem does not have a separate unpublished chart.',
      },
    ],
    howTo: [
      'Keep or edit Salem as pickup, type the drop, add your mobile number, then Get Fare Estimate.',
      'Choose a vehicle and when you want to leave Salem.',
      'Confirm the review so the request uses the same Easy Drop Taxi process as every other city.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Salem?',
        answer:
          'Use the booking card on this page. Pickup is set to Salem. Enter drop and mobile number, estimate the fare, then add vehicle, date and time before you confirm.',
      },
      {
        question: 'Do the priority Salem routes have their own pages?',
        answer:
          'Yes. Salem to Chennai, Coimbatore, Bangalore, Madurai and Trichy each have a landing page. This hub is for other Salem pickups and for travellers who have not chosen a drop yet.',
      },
      {
        question: 'Can I book Salem to Bangalore as one way?',
        answer:
          'Yes. Open Salem to Bangalore Taxi for that pair, or set drop to Bangalore in this form. Keep One Way selected unless you need Round Trip.',
      },
      {
        question: 'Is Chinna Salem the same as Salem?',
        answer:
          'No. Chinna Salem is a different label in the city list. Use Salem unless your actual pickup is Chinna Salem.',
      },
      {
        question: 'Does this page quote a Salem kilometre?',
        answer:
          'No catalogue distance is shown as a fare. The booking form maps the pickup and drop you enter when you request an estimate.',
      },
    ],
    ctaTitle: 'Ready to book a taxi from Salem?',
    ctaBody: 'Enter your drop from Salem and confirm with EasyDropTaxi.',
    priorityRoutes: [
      { origin: 'Salem', destination: 'Chennai', note: 'East toward the capital, with its own pair URL.' },
      { origin: 'Salem', destination: 'Coimbatore', note: 'Southwest toward Easy Drop Taxi’s home city, separate from the Coimbatore catalogue.' },
      { origin: 'Salem', destination: 'Bangalore', note: 'Interstate drop north-west, with a dedicated landing page.' },
      { origin: 'Salem', destination: 'Madurai', note: 'Southbound temple-city drop with its own pair URL.' },
      { origin: 'Salem', destination: 'Trichy', note: 'Southeast toward Tiruchirappalli, with a dedicated landing page.' },
    ],
  },
];

const buildHubPage = (source) => {
  const priorityRoutes = source.priorityRoutes.map(attachRoutePaths);
  return {
    ...source,
    title: `${source.name} Taxi`,
    pickupName: source.pickupName,
    priorityRoutes,
    relatedHubs: [],
  };
};

export const HUB_LANDING_PAGES = HUB_SOURCE.map(buildHubPage);

HUB_LANDING_PAGES.forEach((page) => {
  page.relatedHubs = HUB_LANDING_PAGES
    .filter((item) => item.slug !== page.slug)
    .map((item) => ({ path: item.slug, label: item.title }));
});

export const HUB_LANDING_BY_SLUG = HUB_LANDING_PAGES.reduce((acc, page) => {
  acc[page.slug] = page;
  return acc;
}, {});

export const getHubRelatedLinks = (page) => {
  const links = [];
  const seen = new Set();

  const addLink = (path, label) => {
    if (!path || !label || seen.has(path) || path === page.slug) return;
    seen.add(path);
    links.push({ path, label });
  };

  page.priorityRoutes.forEach((route) => {
    if (route.path) addLink(route.path, route.label);
    if (route.reversePath) {
      addLink(
        route.reversePath,
        `${displayPlaceName(route.destination)} to ${displayPlaceName(route.origin)} Taxi`,
      );
    }
  });

  page.relatedHubs.forEach((hub) => addLink(hub.path, hub.label));
  addLink('/', 'Home');
  addLink('/tariff', 'Tariff');
  return links;
};
